import {
  Badge,
  Button,
  Card,
  CellText,
  DataTable,
  EmptyState,
  FormField,
  ListToolbar,
  Select,
  Switch,
  Text,
  type DataTableColumn,
} from '@ui';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useFacetTexts } from '../../../lib/facets';
import { useColumnSort, useSortMenu } from '../../../lib/useColumnSort';
import { useResetUrlState, useUrlState } from '../../../lib/useUrlState';
import { useSession } from '../../identity/session';
import { formatEuro } from '../../sales/format';
import {
  exportUrl,
  useEntries,
  useFinanceCategories,
  type Entry,
  type EntryFilter,
  type EntryKind,
} from '../api';
import { useEntryRowActions } from '../components/EntryRowActions';
import { FinanceActions, useRecordEntry } from '../components/FinanceActions';
import styles from './FinancePages.module.css';

type KindFilter = 'all' | EntryKind;
const KINDS = ['all', 'income', 'expense'] as const;
const FILTER_KEYS = ['q', 'art', 'kategorie', 'jahr', 'storniert'] as const;
/** URL value of "all years"; the default is the current year. */
const ALL_YEARS = 'alle';

/** Whether an entry matches the search text. */
function matches(e: Entry, needle: string): boolean {
  return [e.description, e.counterparty, e.category.name, e.notes].some((v) =>
    v?.toLocaleLowerCase('de').includes(needle),
  );
}

/** Income and expenses with search, filters and CSV export (FIN-006). */
export function EntriesPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const [, setParams] = useSearchParams();
  const thisYear = String(new Date().getFullYear());
  const [q, setQ] = useUrlState('q', '');
  const [kind] = useUrlState<KindFilter>('art', 'all', KINDS);
  const [category, setCategory] = useUrlState('kategorie', '');
  const [year, setYear] = useUrlState('jahr', thisYear);
  const [cancelled, setCancelled] = useUrlState('storniert', '');
  const resetFilters = useResetUrlState(FILTER_KEYS);
  const columnSort = useColumnSort();
  const facetTexts = useFacetTexts(resetFilters);
  const { record, dialog } = useRecordEntry();
  const entryActions = useEntryRowActions();
  const categories = useFinanceCategories();
  const filter: EntryFilter = {
    from: year === ALL_YEARS ? undefined : `${year}-01-01`,
    to: year === ALL_YEARS ? undefined : `${year}-12-31`,
    kind: kind === 'all' ? undefined : kind,
    category_id: category || undefined,
    include_cancelled: cancelled === '1',
  };
  const entries = useEntries(filter);
  const needle = q.trim().toLocaleLowerCase('de');
  const rows = (entries.data ?? []).filter((e) => !needle || matches(e, needle));
  const counted = rows.filter((e) => !e.cancelled);
  const sum = (k: EntryKind) =>
    counted.filter((e) => e.kind === k).reduce((s, e) => s + e.amount_cents, 0);
  const income = sum('income');
  const expense = sum('expense');
  const categoryName = categories.data?.find((c) => c.id === category)?.name;
  const filtered =
    needle !== '' || kind !== 'all' || category !== '' || year !== thisYear || cancelled !== '';
  const years = Array.from({ length: 6 }, (_, i) => String(Number(thisYear) - i));

  // Switching the kind drops a category of the other kind in the same update.
  const setKind = (next: string) =>
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next === 'all') updated.delete('art');
        else updated.set('art', next);
        const selected = categories.data?.find((c) => c.id === category);
        if (selected && next !== 'all' && selected.kind !== next) updated.delete('kategorie');
        return updated;
      },
      { replace: true },
    );

  const chips = [
    ...(category
      ? [{ key: 'category', label: categoryName ?? '…', onRemove: () => setCategory('') }]
      : []),
    ...(year !== thisYear
      ? [
          {
            key: 'year',
            label: year === ALL_YEARS ? t('finance.filters.allYears') : year,
            onRemove: () => setYear(thisYear),
          },
        ]
      : []),
    ...(cancelled
      ? [
          {
            key: 'cancelled',
            label: t('finance.filters.withCancelled'),
            onRemove: () => setCancelled(''),
          },
        ]
      : []),
  ];

  const columns: DataTableColumn<Entry>[] = [
    {
      key: 'date',
      header: t('finance.list.date'),
      width: '7rem',
      cell: (e) => formatDate(e.booked_on),
      sortValue: (e) => e.booked_on,
    },
    {
      key: 'description',
      header: t('finance.list.description'),
      primary: true,
      cell: (e) => (
        <CellText
          main={
            <>
              {e.description}
              {e.cancelled && (
                <>
                  {' '}
                  <Badge size="sm">{t('finance.cancelled')}</Badge>
                </>
              )}
            </>
          }
          sub={[
            e.counterparty,
            e.source.kind !== 'manual' ? t(`finance.source.${e.source.kind}`) : null,
            e.receipt ? t('finance.receipt.has') : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        />
      ),
      sortValue: (e) => e.description,
    },
    {
      key: 'category',
      header: t('finance.list.category'),
      cell: (e) => e.category.name,
      sortValue: (e) => e.category.name,
    },
    {
      key: 'method',
      header: t('finance.list.method'),
      mobile: 'hidden',
      cell: (e) => (e.method ? t(`finance.method.${e.method}`) : '–'),
      sortValue: (e) => (e.method ? t(`finance.method.${e.method}`) : null),
    },
    {
      key: 'amount',
      header: t('finance.list.amount'),
      align: 'end',
      cell: (e) => {
        const amount = `${e.kind === 'income' ? '+' : '−'}${formatEuro(e.amount_cents)}`;
        return e.cancelled ? <s>{amount}</s> : amount;
      },
      sortValue: (e) => (e.kind === 'income' ? e.amount_cents : -e.amount_cents),
    },
  ];

  const sortMenu = useSortMenu(columns, rows, columnSort);

  return (
    <div className={styles.page}>
      <FinanceActions onRecord={record} exportHref={exportUrl(filter)} />
      <ListToolbar
        sort={sortMenu}
        facets={{
          ...facetTexts,
          items: [
            {
              type: 'select',
              key: 'category',
              label: t('finance.filters.category'),
              value: category,
              options: (categories.data ?? [])
                .filter((c) => kind === 'all' || c.kind === kind)
                .map((c) => ({
                  value: c.id,
                  label: kind === 'all' ? `${c.name} (${t(`finance.kind.${c.kind}`)})` : c.name,
                })),
              onChange: setCategory,
            },
            {
              type: 'select',
              key: 'year',
              label: t('finance.filters.year'),
              value: year,
              neutral: thisYear,
              options: [
                ...(years.includes(year) || year === ALL_YEARS
                  ? []
                  : [{ value: year, label: year }]),
                ...years.map((y) => ({ value: y, label: y })),
                { value: ALL_YEARS, label: t('finance.filters.allYears') },
              ],
              onChange: setYear,
            },
            {
              type: 'toggle',
              key: 'cancelled',
              label: t('finance.filters.cancelled'),
              active: cancelled === '1',
              onChange: (on) => setCancelled(on ? '1' : ''),
            },
          ],
        }}
        search={{
          value: q,
          onChange: setQ,
          label: t('common.search'),
          clearLabel: t('common.clear'),
        }}
        scope={{
          label: t('finance.filters.kind'),
          value: kind,
          onChange: setKind,
          options: [
            { value: 'all', label: t('finance.filters.all') },
            { value: 'income', label: t('finance.overview.income') },
            { value: 'expense', label: t('finance.overview.expenses') },
          ],
        }}
        filters={{
          label: t('common.filter'),
          activeCount: chips.length,
          content: (
            <>
              <FormField label={t('finance.filters.category')}>
                {(p) => (
                  <Select
                    {...p}
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    options={[
                      { value: '', label: t('finance.filters.allCategories') },
                      ...(categories.data ?? [])
                        .filter((c) => kind === 'all' || c.kind === kind)
                        .map((c) => ({
                          value: c.id,
                          label:
                            kind === 'all' ? `${c.name} (${t(`finance.kind.${c.kind}`)})` : c.name,
                        })),
                    ]}
                  />
                )}
              </FormField>
              <FormField label={t('finance.filters.year')}>
                {(p) => (
                  <Select
                    {...p}
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    options={[
                      ...(years.includes(year) || year === ALL_YEARS
                        ? []
                        : [{ value: year, label: year }]),
                      ...years.map((y) => ({ value: y, label: y })),
                      { value: ALL_YEARS, label: t('finance.filters.allYears') },
                    ]}
                  />
                )}
              </FormField>
              <Switch
                label={t('finance.filters.cancelled')}
                checked={cancelled === '1'}
                onChange={(e) => setCancelled(e.target.checked ? '1' : '')}
              />
            </>
          ),
        }}
        chips={{
          chips,
          removeLabel: (label) => t('common.removeFilter', { label }),
          resetLabel: t('common.resetAll'),
          onReset: resetFilters,
        }}
      />
      <Card padding="none">
        {entries.error ? (
          <Text tone="danger" role="alert" style={{ padding: 'var(--space-4)' }}>
            {errorMessage(entries.error)}
          </Text>
        ) : (
          <DataTable
            caption={t('finance.list.caption')}
            rows={rows}
            rowKey={(e) => e.id}
            rowHref={(e) => `/finanzen/buchungen/${e.id}`}
            rowActions={entryActions.rowActions}
            {...columnSort}
            loading={entries.isPending}
            columns={columns}
            empty={
              filtered ? (
                <EmptyState
                  title={t('common.noResults')}
                  actions={
                    <Button variant="secondary" onClick={resetFilters}>
                      {t('common.resetFilters')}
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  title={t('finance.list.emptyYear', { year })}
                  actions={
                    can('finance.write') && (
                      <Button onClick={() => record('expense')}>
                        {t('finance.quick.expense')}
                      </Button>
                    )
                  }
                />
              )
            }
          />
        )}
      </Card>
      {counted.length > 0 && (
        <Text size="sm" tone="muted" align="end" numeric aria-live="polite">
          {t('finance.overview.income')} {formatEuro(income)} · {t('finance.overview.expenses')}{' '}
          {formatEuro(expense)} · {t('finance.overview.result')} {formatEuro(income - expense)}
        </Text>
      )}
      {dialog}
      {entryActions.dialogs}
    </div>
  );
}
